import { FullFormState, IViewModelContainer } from "../../Engine/Core/Store/FullFormState";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../Common/ModelPath/ModelPath";
import { BindingPath } from "../../Engine/Core/CoreTypes";
import { FormSettings } from "../../Forms/Types/FormSettings";
import { FeatureFlags } from "../../Forms/Types/FeatureFlags";
import { VariablesContextData } from "../../Controls/Services/Contexts/VariablesContext";

export type PrimitiveValue = string | number | boolean;
function isPrimitiveValue(value: unknown): value is PrimitiveValue {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

const pathPrefixes = {
    featureFlags: "@featureFlags/",
    settings: "@settings/",
    contextVars: "@contextVars/",
    mergedPropertiesForBackendCompatibility: "@mergedPropertiesForBackendCompatibility/",
    all: "@",
} as const;

function hasPrefix(path: string, prefix: string) {
    return path.startsWith(prefix);
}

function getFeatureFlagByPath(state: FullFormState, path: string): PrimitiveValue | undefined {
    let value = undefined;
    const featureFlagValue = state?.featureFlags?.[path.replace(pathPrefixes.featureFlags, "") as keyof FeatureFlags];
    if (featureFlagValue == undefined) {
        value = undefined;
    } else if (isPrimitiveValue(featureFlagValue)) {
        value = featureFlagValue;
    } else {
        // eslint-disable-next-line no-console
        console.warn(`FeatureFlags of type "${typeof featureFlagValue}" is not supported`);
    }
    return value;
}

function getSettingByPath(state: FullFormState, path: string): PrimitiveValue | undefined {
    let value = undefined;
    const settingValue = state.settings?.[path.replace(pathPrefixes.settings, "") as keyof FormSettings];
    if (settingValue == undefined) {
        value = undefined;
    } else if (isPrimitiveValue(settingValue)) {
        value = settingValue;
    } else {
        // eslint-disable-next-line no-console
        console.warn(`Setting of type "${typeof settingValue}" is not supported`);
    }
    return value;
}

function getContextVar(variableContext: VariablesContextData, path: string): PrimitiveValue | undefined {
    const varName = path.replace(/^\@contextVars\//, "");
    const variable = variableContext[varName];
    return variable?.toString();
}

export type ViewModelValueSelect = (
    viewModelContainer: IViewModelContainer,
    path: BindingPath | AbsoluteModelPath,
    customValueFieldName: string
) => string;

export function getExternalPropertyValue(
    state: FullFormState,
    variableContext: VariablesContextData,
    path: string
): PrimitiveValue | undefined {
    if (path.startsWith("@mergedPropertiesForBackendCompatibility/")) {
        // Что это за история: на бекенде getProperty не принимает тип внешней переменной -- просто имя, они какбы
        // сливаются в одну кучу (ну или так было), поэтому для обраной совместимости может приходит такой префикс
        // когда мы потеряли контекс.
        const name = path.replace(pathPrefixes.mergedPropertiesForBackendCompatibility, "");
        return (
            getFeatureFlagByPath(state, pathPrefixes.featureFlags + name) ??
            getSettingByPath(state, pathPrefixes.settings + name) ??
            state?.requisites?.["@" + name]?.value ??
            getContextVar(variableContext, pathPrefixes.contextVars + name) ??
            ""
        );
    } else if (hasPrefix(path, pathPrefixes.featureFlags)) {
        return getFeatureFlagByPath(state, path) ?? "";
    } else if (hasPrefix(path, pathPrefixes.settings)) {
        return getSettingByPath(state, path) ?? "";
    } else if (hasPrefix(path, pathPrefixes.contextVars)) {
        return getContextVar(variableContext, path) ?? "";
    } else if (hasPrefix(path, pathPrefixes.all)) {
        return state?.requisites?.[path]?.value ?? "";
    }
    return undefined;
}

export function getValueByPath(
    state: FullFormState,
    selector: ViewModelValueSelect,
    variableContext: VariablesContextData,
    path: AbsoluteResolvedModelPath | string,
    name: string = "value"
): PrimitiveValue {
    let value;
    if (typeof path === "string" && path.startsWith(pathPrefixes.contextVars)) {
        const varName = path.replace(/^\@contextVars\//, "");
        const variable = variableContext[varName];
        value = variable?.toString();
    } else if (typeof path === "string") {
        value = getExternalPropertyValue(state, variableContext, path);
    } else {
        value = selector(state, path, name);
    }

    return value ?? "";
}
